import { DEFAULT_LANGUAGE, normalizeSupportedLanguage, type SupportedLanguage } from '@/lib/user-language'
import { en, type EnglishDictionary } from './dictionaries/en'
import { es } from './dictionaries/es'

export type Dictionary = EnglishDictionary
export type DictionaryValue = string | { readonly [key: string]: DictionaryValue }

export const dictionaries = {
  en,
  es,
} as const satisfies Record<SupportedLanguage, Dictionary>

export type TranslationKey = ReturnType<typeof flattenDictionary>[number]

export function getDictionary(language: unknown): Dictionary {
  return dictionaries[normalizeSupportedLanguage(language)]
}

export function t(dictionary: Dictionary, key: string, replacements: Record<string, string | number> = {}): string {
  const value = key.split('.').reduce<DictionaryValue | undefined>((current, segment) => {
    if (!current || typeof current === 'string') return undefined
    return current[segment]
  }, dictionary)

  if (typeof value !== 'string') return key

  return Object.entries(replacements).reduce(
    (text, [name, replacement]) => text.replaceAll(`{${name}}`, String(replacement)),
    value,
  )
}

export function flattenDictionary(dictionary: Dictionary): string[] {
  const keys: string[] = []

  function visit(value: DictionaryValue, path: string[]) {
    if (typeof value === 'string') {
      keys.push(path.join('.'))
      return
    }

    for (const [childKey, childValue] of Object.entries(value)) {
      visit(childValue, [...path, childKey])
    }
  }

  visit(dictionary, [])
  return keys
}

export function defaultI18n() {
  const dictionary = getDictionary(DEFAULT_LANGUAGE)
  return {
    language: DEFAULT_LANGUAGE,
    dictionary,
    t: (key: string, replacements?: Record<string, string | number>) => t(dictionary, key, replacements),
  }
}
